import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useContent } from '../content';
import { calculateBeyondTestResults } from '../utils/beyondTestLogic';
import PremiumPageGate from '../components/premium/PremiumPageGate';
import { usePremiumAccess } from '../monetization/usePremiumAccess';

const BeyondPaidVersion = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const access = usePremiumAccess('beyondPremium');
    const { stats, loading } = access;
    const { getC, loaded: contentLoaded } = useContent(['beyond']);

    if (loading || !contentLoaded) return <div style={styles.container}>{t('common.loading')}</div>;

    const result = stats?.beyond;

    if (!result) {
        return (
            <div style={styles.container}>
                <div style={styles.content}>
                    <button onClick={() => navigate('/mypage')} style={styles.backButton}>← {t('common.back')}</button>
                    <div style={styles.mainCard}>
                        <p style={{ textAlign: 'center', padding: '2rem', color: '#fca5a5' }}>
                            {t('payment.cant_analyze') || '분석 데이터를 찾을 수 없습니다.'}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Calculate scores or use default
    const scores = result.scores || (result.payload?.answers ? calculateBeyondTestResults(result.payload.answers).scores : { S: 50, E: 50, B: 50 });

    // Extract S, E, B types from payload or summary
    // Structure: result.summary.S, result.summary.E, result.summary.B (e.g., 'A', 'B', 'C', 'D')
    const sType = result.summary?.S;
    const eType = result.summary?.E;
    const bType = result.summary?.B;
    const sData = getC(`beyond.beyond_test.results.S.${sType}`);
    const eData = getC(`beyond.beyond_test.results.E.${eType}`);

    return (
        <PremiumPageGate access={access}>
            <div style={styles.container}>
                <div style={styles.content}>
                    <button onClick={() => navigate('/mypage')} style={styles.backButton}>← {t('common.back')}</button>

                    <h1 style={styles.headerTitle}>Beyond Test Premium Report</h1>
                    <p style={styles.headerSubtitle}>
                        당신이 진정으로 추구하는 이상향(Ideal)을 시각화하고 심층 분석한 결과입니다.
                    </p>

                    <div style={styles.mainCard}>
                        <div style={styles.badge}>PREMIUM BEYOND REPORT</div>
                        <h2 style={styles.resultTitle}>{sType}{eType}{bType}</h2>

                        <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem', marginBottom: '2rem' }}>
                            * 각 지표의 0%와 100%는 좋고 나쁨이 아닌, <strong>서로 다른 성향의 방향성</strong>을 나타냅니다.<br />
                            (0%에 가까울수록 A유형, 100%에 가까울수록 D유형의 특성이 강합니다.)
                        </p>

                        <div style={{ margin: '2rem 0', borderTop: '1px solid rgba(255,255,255,0.1)' }} />

                        {/* S-Ideal Analysis */}
                        {sData && (
                            <div style={styles.premiumAxisCard}>
                                <div style={{ ...styles.glowBackground, background: 'radial-gradient(circle at 0% 0%, rgba(167, 139, 250, 0.15) 0%, transparent 50%)' }} />

                                <div style={styles.axisHeader}>
                                    <div style={{ ...styles.axisIconContainer, background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' }}>💎</div>
                                    <div>
                                        <h3 style={{ ...styles.axisTitle, color: '#a78bfa' }}>S-Ideal: 정서적 낙원 (Inner Climate)</h3>
                                        <p style={styles.axisSubtitle}>"당신이 꿈꾸는 내면의 가장 고요하고 완벽한 기상 상태"</p>
                                    </div>
                                </div>

                                <div style={styles.scoreContainer}>
                                    <div style={styles.scoreHeader}>
                                        <span style={styles.scoreLabel}>Personal Score</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                            <span style={{ color: '#a78bfa', fontWeight: '900', fontSize: '1.8rem', textShadow: '0 0 15px rgba(167,139,250,0.4)' }}>{scores.S}%</span>
                                            <span style={{ ...styles.typeBadge, background: 'rgba(167, 139, 250, 0.15)', color: '#c084fc', border: '1px solid rgba(167,139,250,0.3)' }}>Type {sType}</span>
                                        </div>
                                    </div>
                                    <div style={styles.scoreBarBg}>
                                        <div style={{ ...styles.scoreBarFill, width: `${scores.S}%`, background: 'linear-gradient(90deg, #8b5cf6, #d8b4fe)', boxShadow: '0 0 15px rgba(167,139,250,0.6)' }} />
                                        <div style={{ position: 'absolute', left: '33%', top: 0, bottom: 0, width: '1px', background: 'rgba(255,255,255,0.2)' }} />
                                        <div style={{ position: 'absolute', left: '66%', top: 0, bottom: 0, width: '1px', background: 'rgba(255,255,255,0.2)' }} />
                                    </div>
                                    <div style={styles.spectrumLabels}>
                                        <div style={{ ...styles.spectrumLabel, textAlign: 'left' }}>
                                            <span style={{ color: '#c084fc', fontWeight: 'bold' }}>Type A</span>
                                            <br /><span style={styles.spectrumLabelText}>{getC('beyond.beyond_test.results.S.A.title').split(' (')[0]}</span>
                                        </div>
                                        <div style={{ ...styles.spectrumLabel, textAlign: 'center' }}>
                                            <span style={{ color: '#c084fc', fontWeight: 'bold' }}>Type B</span>
                                            <br /><span style={styles.spectrumLabelText}>{getC('beyond.beyond_test.results.S.B.title').split(' (')[0]}</span>
                                        </div>
                                        <div style={{ ...styles.spectrumLabel, textAlign: 'center' }}>
                                            <span style={{ color: '#c084fc', fontWeight: 'bold' }}>Type C</span>
                                            <br /><span style={styles.spectrumLabelText}>{getC('beyond.beyond_test.results.S.C.title').split(' (')[0]}</span>
                                        </div>
                                        <div style={{ ...styles.spectrumLabel, textAlign: 'right' }}>
                                            <span style={{ color: '#c084fc', fontWeight: 'bold' }}>Type D</span>
                                            <br /><span style={styles.spectrumLabelText}>{getC('beyond.beyond_test.results.S.D.title').split(' (')[0]}</span>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ ...styles.insightBox, borderLeftColor: '#a78bfa' }}>
                                    <div style={{ ...styles.insightTitle, color: '#e9d5ff' }}>
                                        <span style={{ fontSize: '1.2rem' }}>✨</span> 당신의 결과: {sData.title}
                                    </div>
                                    <p style={styles.insightText}>{sData.desc}</p>
                                </div>
                            </div>
                        )}

                        {/* E-Ideal Analysis */}
                        {eData && (
                            <div style={styles.premiumAxisCard}>
                                <div style={{ ...styles.glowBackground, background: 'radial-gradient(circle at 100% 0%, rgba(244, 114, 182, 0.15) 0%, transparent 50%)' }} />

                                <div style={styles.axisHeader}>
                                    <div style={{ ...styles.axisIconContainer, background: 'linear-gradient(135deg, #ec4899, #f472b6)' }}>📡</div>
                                    <div>
                                        <h3 style={{ ...styles.axisTitle, color: '#f472b6' }}>E-Ideal: 관계의 거리 (Connection Texture)</h3>
                                        <p style={styles.axisSubtitle}>"현실의 눈치를 벗어던졌을 때, 당신이 도달하고 싶은 영혼의 밀착도"</p>
                                    </div>
                                </div>

                                <div style={styles.scoreContainer}>
                                    <div style={styles.scoreHeader}>
                                        <span style={styles.scoreLabel}>Personal Score</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                            <span style={{ color: '#f472b6', fontWeight: '900', fontSize: '1.8rem', textShadow: '0 0 15px rgba(244,114,182,0.4)' }}>{scores.E}%</span>
                                            <span style={{ ...styles.typeBadge, background: 'rgba(244, 114, 182, 0.15)', color: '#f9a8d4', border: '1px solid rgba(244,114,182,0.3)' }}>Type {eType}</span>
                                        </div>
                                    </div>
                                    <div style={styles.scoreBarBg}>
                                        <div style={{ ...styles.scoreBarFill, width: `${scores.E}%`, background: 'linear-gradient(90deg, #ec4899, #fbcfe8)', boxShadow: '0 0 15px rgba(244,114,182,0.6)' }} />
                                        <div style={{ position: 'absolute', left: '33%', top: 0, bottom: 0, width: '1px', background: 'rgba(255,255,255,0.2)' }} />
                                        <div style={{ position: 'absolute', left: '66%', top: 0, bottom: 0, width: '1px', background: 'rgba(255,255,255,0.2)' }} />
                                    </div>
                                    <div style={styles.spectrumLabels}>
                                        <div style={{ ...styles.spectrumLabel, textAlign: 'left' }}>
                                            <span style={{ color: '#f9a8d4', fontWeight: 'bold' }}>Type A</span>
                                            <br /><span style={styles.spectrumLabelText}>{getC('beyond.beyond_test.results.E.A.title').split(' (')[0]}</span>
                                        </div>
                                        <div style={{ ...styles.spectrumLabel, textAlign: 'center' }}>
                                            <span style={{ color: '#f9a8d4', fontWeight: 'bold' }}>Type B</span>
                                            <br /><span style={styles.spectrumLabelText}>{getC('beyond.beyond_test.results.E.B.title').split(' (')[0]}</span>
                                        </div>
                                        <div style={{ ...styles.spectrumLabel, textAlign: 'center' }}>
                                            <span style={{ color: '#f9a8d4', fontWeight: 'bold' }}>Type C</span>
                                            <br /><span style={styles.spectrumLabelText}>{getC('beyond.beyond_test.results.E.C.title').split(' (')[0]}</span>
                                        </div>
                                        <div style={{ ...styles.spectrumLabel, textAlign: 'right' }}>
                                            <span style={{ color: '#f9a8d4', fontWeight: 'bold' }}>Type D</span>
                                            <br /><span style={styles.spectrumLabelText}>{getC('beyond.beyond_test.results.E.D.title').split(' (')[0]}</span>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ ...styles.insightBox, borderLeftColor: '#f472b6' }}>
                                    <div style={{ ...styles.insightTitle, color: '#fbcfe8' }}>
                                        <span style={{ fontSize: '1.2rem' }}>✨</span> 당신의 결과: {eData.title}
                                    </div>
                                    <p style={styles.insightText}>{eData.desc}</p>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </PremiumPageGate>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        color: '#fff',
        padding: '2rem 1rem',
        paddingTop: '6rem',
    },
    content: {
        maxWidth: '840px',
        margin: '0 auto',
    },
    backButton: {
        backgroundColor: 'transparent',
        border: 'none',
        color: '#9ca3af',
        cursor: 'pointer',
        fontSize: '1.05rem',
        marginBottom: '2rem',
        display: 'flex',
        alignItems: 'center',
        padding: 0,
        transition: 'color 0.2s',
    },
    headerTitle: {
        fontSize: '2.4rem',
        fontWeight: '900',
        marginBottom: '0.8rem',
        background: 'linear-gradient(to right, #e2e8f0, #94a3b8)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        letterSpacing: '-0.5px',
    },
    headerSubtitle: {
        color: '#94a3b8',
        marginBottom: '3rem',
        fontSize: '1.15rem',
        lineHeight: '1.6',
    },
    mainCard: {
        background: 'rgba(15, 23, 42, 0.4)',
        borderRadius: '32px',
        padding: '3rem 2rem',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        boxShadow: '0 25px 60px rgba(0, 0, 0, 0.6)',
    },
    badge: {
        display: 'inline-block',
        padding: '0.5rem 1.2rem',
        backgroundColor: 'rgba(167, 139, 250, 0.1)',
        color: '#c084fc',
        borderRadius: '100px',
        fontSize: '0.8rem',
        fontWeight: 'bold',
        letterSpacing: '0.15em',
        marginBottom: '2rem',
        border: '1px solid rgba(167, 139, 250, 0.25)',
        boxShadow: '0 0 15px rgba(167, 139, 250, 0.15)',
    },
    resultTitle: {
        fontSize: '3.5rem',
        fontWeight: '900',
        marginBottom: '1.5rem',
        letterSpacing: '8px',
        textAlign: 'center',
        background: 'linear-gradient(to right, #a78bfa, #f472b6, #fbbf24)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textShadow: '0 10px 30px rgba(244, 114, 182, 0.2)',
    },
    premiumAxisCard: {
        background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.9) 100%)',
        borderRadius: '24px',
        padding: '2.5rem',
        marginBottom: '2.5rem',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 15px 35px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
        position: 'relative',
        overflow: 'hidden',
    },
    glowBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        opacity: 0.6,
        zIndex: 0,
    },
    axisHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '1.2rem',
        marginBottom: '2rem',
        position: 'relative',
        zIndex: 1,
    },
    axisIconContainer: {
        width: '56px',
        height: '56px',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.8rem',
        boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.1)',
        flexShrink: 0,
    },
    axisTitle: {
        fontSize: '1.5rem',
        fontWeight: '800',
        marginBottom: '0.4rem',
        letterSpacing: '-0.5px',
        margin: 0,
    },
    axisSubtitle: {
        fontSize: '0.95rem',
        color: '#94a3b8',
        fontStyle: 'italic',
        lineHeight: '1.5',
        margin: 0,
    },
    scoreContainer: {
        background: 'rgba(0, 0, 0, 0.4)',
        padding: '2rem',
        borderRadius: '20px',
        marginBottom: '2rem',
        border: '1px solid rgba(255, 255, 255, 0.04)',
        position: 'relative',
        zIndex: 1,
    },
    scoreHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
    },
    scoreLabel: {
        color: '#94a3b8',
        fontSize: '0.9rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '1px',
    },
    typeBadge: {
        padding: '0.3rem 0.8rem',
        borderRadius: '8px',
        fontSize: '0.85rem',
        fontWeight: 'bold',
    },
    scoreBarBg: {
        width: '100%',
        height: '10px',
        backgroundColor: 'rgba(255,255,255,0.08)',
        borderRadius: '5px',
        marginBottom: '1.2rem',
        position: 'relative',
    },
    scoreBarFill: {
        height: '100%',
        borderRadius: '5px',
        transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    spectrumLabels: {
        display: 'flex',
        justifyContent: 'space-between',
        position: 'relative',
    },
    spectrumLabel: {
        fontSize: '0.8rem',
        color: '#64748b',
        lineHeight: '1.5',
        width: '25%', // Ensure equal distribution
        padding: '0 0.5rem',
        boxSizing: 'border-box',
    },
    spectrumLabelText: {
        display: 'inline-block',
        marginTop: '0.3rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
    },
    insightBox: {
        background: 'rgba(255, 255, 255, 0.03)',
        borderLeft: '4px solid', // Color inherited dynamically
        padding: '1.8rem',
        borderRadius: '0 16px 16px 0',
        position: 'relative',
        zIndex: 1,
        backdropFilter: 'blur(10px)',
    },
    insightTitle: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        marginBottom: '0.8rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    insightText: {
        fontSize: '1.05rem',
        color: '#cbd5e1',
        lineHeight: '1.8',
        margin: 0,
    }
};

export default BeyondPaidVersion;
